import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import ConnectionObjectSelect, { useConnectionObject } from '../Components/ConnectionObjectSelect';
import { CustomNumberInput, CustomSelect, CustomToggle } from '../Components/CustomFormElements';
import ItemsPickerModal from '../Components/ItemsPickerModal';
import ListSelect from '../Components/ListSelect';
import PageLoader from '../Components/PageLoader';
import { RecordRouteApiProvider, useRecordRouteApi } from '../Services/RecordRouteApiProvider';
import { formatBytes, getExtension } from '../utils/fileUtil';
import { isEmpty } from '../utils/validationUtil';
import RecordRouteConnectionObjectMapping from './RecordRouteConnectionObjectMapping';
import { useAuth } from '../Contexts/AuthContext';

const googleDriveFileColumns = [
  {
    key: 'name',
    header: 'Name',
    widthClass: 'items-picker-col--flex-3',
    render: (item) => (
      <>
        <div
          className={
            item.viewLink ? 'items-picker-name items-picker-name--linked' : 'items-picker-name'
          }
          title={item.name || ''}
          onClick={() => {
            if (item.viewLink) {
              window.open(item.viewLink, '_blank', 'noreferrer');
            }
          }}
        >
          {item.name}
        </div>
        <div className="items-picker-id small-font">id: {item.id}</div>
      </>
    ),
    searchable: true,
    getSearchValue: (item) => item.name || '',
  },
  {
    key: 'ext',
    header: 'Ext',
    widthClass: 'items-picker-col--flex-1',
    render: (item) => (
      <div className="items-picker-description small-font">
        {getExtension(item.name, item.mimeType)}
      </div>
    ),
  },
  {
    key: 'size',
    header: 'Size',
    widthClass: 'items-picker-col--flex-1',
    render: (item) => (
      <div className="items-picker-description small-font">{formatBytes(item.size)}</div>
    ),
  },
  {
    key: 'modified',
    header: 'Modified',
    widthClass: 'items-picker-col--flex-2',
    render: (item) => (
      <div className="items-picker-description small-font">
        {item.modifiedTime ? new Date(item.modifiedTime).toLocaleString() : '-'}
      </div>
    ),
  },
];

function RecordRouteDetail({ canEdit }) {
  const navigate = useNavigate();
  const { organizationId, recordRouteId } = useParams();
  const { showAlert } = useAlert();

  const {
    createRecordRoute,
    updateRecordRouteById,
    getRecordRouteById,
    getChannels,
    getAgents,
    getDocuments,
    getDocumentCollections,
    getPackageCollections,
    getZendeskConnections,
    getCreatioConnections,
    getNotionConnections,
    triggerEventById,
    getGoogleConnections,
    getGoogleDriveFiles,
    getIntercomConnections,
  } = useRecordRouteApi();

  const { status } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [isActive, setIsActive] = useState(true);
  const [routeEventType, setRouteEventType] = useState();
  const [fromType, setFromType] = useState();
  const [toType, setToType] = useState();
  const [fromKey, setFromKey] = useState();
  const [toKey, setToKey] = useState();
  const [priorityIndex, setPriorityIndex] = useState();

  const [channels, setChannels] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [documentCollections, setDocumentCollections] = useState([]);
  const [informationPackageCollections, setInformationPackageCollections] = useState([]);
  const [creatioConnections, setCreatioConnections] = useState([]);
  const [zendeskConnections, setZendeskConnections] = useState([]);
  const [notionConnections, setNotionConnections] = useState([]);
  const [googleConnections, setGoogleConnections] = useState([]);
  const [intercomConnections, setIntercomConnections] = useState([]);
  const [isGoogleDriveFilesModalOpen, setIsGoogleDriveFilesModalOpen] = useState(false);
  const [selectedGoogleDriveFiles, setSelectedGoogleDriveFiles] = useState([]);

  const [routeProperties, setRouteProperties] = useState({});
  const [fromConnectionObjectMapping, setFromConnectionObjectMapping] = useState({});
  const [toConnectionObjectMapping, setToConnectionObjectMapping] = useState({});

  const {
    connectionObject: selectedFromConnectionObject,
    isLoading: isFromConnectionObjectLoading,
  } = useConnectionObject(fromType, fromKey);
  const { connectionObject: selectedToConnectionObject, isLoading: isToConnectionObjectLoading } =
    useConnectionObject(toType, toKey);

  const isDocumentFlag = status?.feature_flags?.DOCUMENT === true;
  const isInformationPackageFlag = status?.feature_flags?.INFORMATION_PACKAGE === true;
  const isConnectionFlag = status?.feature_flags?.CONNECTION === true;

  const types = [
    { value: 'channel', label: 'Channel' },
    { value: 'assistant', label: 'Assistant' },

    ...(isDocumentFlag
      ? [
          { value: 'document', label: 'Document' },
          { value: 'document_collection', label: 'Document Collection' },
        ]
      : []),

    ...(isInformationPackageFlag
      ? [{ value: 'package_collection', label: 'Information Package Collection' }]
      : []),

    ...(isConnectionFlag ? [{ value: 'connection_object', label: 'Connection Object' }] : []),

    { value: 'lead', label: 'Lead' },
    { value: 'article', label: 'Article' },
    { value: 'notion', label: 'Notion' },
    { value: 'google_drive', label: 'Google Drive' },
  ];
  const isTypeDisabled = (type) => {
    if (['document', 'document_collection'].includes(type)) {
      return !isDocumentFlag;
    }

    if (type === 'package_collection') {
      return !isInformationPackageFlag;
    }

    if (type === 'connection_object') {
      return !isConnectionFlag;
    }

    return false;
  };

  useEffect(() => {
    const func = async () => {
      setLoading(true);

      const skip = 0;
      const limit = -1; // Negative limit returns all values.

      const loaders = [
        [getChannels, setChannels],
        [getAgents, setAssistants],
        [getDocuments, setDocuments],
        [getDocumentCollections, setDocumentCollections],
        [getPackageCollections, setInformationPackageCollections],
        [getCreatioConnections, setCreatioConnections],
        [getZendeskConnections, setZendeskConnections],
        [getNotionConnections, setNotionConnections],
        [getGoogleConnections, setGoogleConnections],
        [getIntercomConnections, setIntercomConnections],
      ];

      loaders.forEach(([fetchData, setLocalState]) => {
        fetchData(skip, limit).then((data) => setLocalState(data ?? []));
      });
      try {
        if (recordRouteId !== 'new-record-route') {
          const response = await getRecordRouteById(recordRouteId);
          setIsActive(response.is_active);
          setToType(response.to_type);
          setToKey(response.to_key);
          setFromType(response.from_type);
          setFromKey(response.from_key);
          setRouteEventType(response.route_event_type);
          setPriorityIndex(response.priority_index);
          setSelectedGoogleDriveFiles(
            (response.google_drive_files ?? []).map((googleDriveFile) => ({
              id: googleDriveFile.id,
              name: googleDriveFile.name,
              mimeType: googleDriveFile.mime_type,
              size: googleDriveFile.size,
              modifiedTime: googleDriveFile.modified_at,
              viewLink: googleDriveFile.view_link,
            })),
          );

          const props = response.properties || {};
          setRouteProperties(props);

          const objectMapper = response.object_mapper || {};
          setFromConnectionObjectMapping(
            objectMapper.from_connection_object_mapping ??
              props.from_connection_object_mapping ??
              {},
          );
          setToConnectionObjectMapping(
            objectMapper.to_connection_object_mapping ?? props.to_connection_object_mapping ?? {},
          );
        }
      } catch (error) {
        setError('Error fetching record route data');
        console.error('Error fetching record route data:', error);
      } finally {
        setLoading(false);
      }
    };
    func();
  }, [recordRouteId]);

  useEffect(() => {
    setIsNew(recordRouteId === 'new-record-route');
    setIsDisabled(!canEdit);
  }, [recordRouteId]);

  const fetchGoogleDriveFiles = useCallback(
    async ({ limit, pageToken, searchQuery } = {}) => {
      if (!fromKey) {
        return { items: [], nextPageToken: null, hasMore: false };
      }

      try {
        const response = await getGoogleDriveFiles(fromKey, {
          limit: limit ?? 100,
          pageToken: pageToken ?? null,
          searchQuery: searchQuery ?? null,
        });
        const fetchedGoogleDriveFiles = (response.items ?? []).map((googleDriveFile) => ({
          id: googleDriveFile.id,
          name: googleDriveFile.name,
          mimeType: googleDriveFile.mime_type,
          size: googleDriveFile.size,
          modifiedTime: googleDriveFile.modified_at,
          viewLink: googleDriveFile.links?.view,
        }));
        const selectedGoogleDriveFileIds = new Set(
          selectedGoogleDriveFiles.map((googleDriveFile) => googleDriveFile.id),
        );
        const items =
          pageToken || searchQuery
            ? fetchedGoogleDriveFiles
            : [
                ...selectedGoogleDriveFiles,
                ...fetchedGoogleDriveFiles.filter(
                  (googleDriveFile) => !selectedGoogleDriveFileIds.has(googleDriveFile.id),
                ),
              ];

        return {
          items,
          nextPageToken: response.next_page_token ?? null,
          hasMore: Boolean(response.has_more),
        };
      } catch (error) {
        console.error('Error fetching Google Drive files:', error);
        showAlert('fail', 'Failed to load Google Drive files');
        return { items: [], nextPageToken: null, hasMore: false };
      }
    },
    [fromKey, getGoogleDriveFiles, selectedGoogleDriveFiles, showAlert],
  );

  const handleSaveGoogleDriveFiles = useCallback((googleDriveFiles) => {
    setSelectedGoogleDriveFiles(googleDriveFiles);
    setIsGoogleDriveFilesModalOpen(false);
  }, []);

  const onSave = async () => {
    if (isEmpty(routeEventType)) {
      showAlert('fail', 'Route event type is required');
      return;
    }
    if (isEmpty(fromType)) {
      showAlert('fail', 'From type is required');
      return;
    }
    if (isEmpty(toType)) {
      showAlert('fail', 'To type is required');
      return;
    }
    if (isEmpty(toKey)) {
      showAlert('fail', 'To key is required');
      return;
    }

    if (isEmpty(fromKey)) {
      showAlert('fail', 'From key is required');
      return;
    }

    const isGoogleDriveInformationPackageCollectionRoute =
      fromType === 'google_drive' && toType === 'package_collection';

    if (isGoogleDriveInformationPackageCollectionRoute && selectedGoogleDriveFiles.length === 0) {
      showAlert('fail', 'Select at least one Google Drive file for Information Package Collection');
      return;
    }

    if (isGoogleDriveInformationPackageCollectionRoute && selectedGoogleDriveFiles.length > 100) {
      showAlert('fail', 'Select no more than 100 Google Drive files');
      return;
    }

    const updatedProperties = routeProperties || {};

    const objectMapper = {
      from_connection_object_mapping: fromConnectionObjectMapping,
      to_connection_object_mapping: toConnectionObjectMapping,
    };

    let recordRoute = {
      route_event_type: routeEventType,
      from_key: fromKey,
      to_key: toKey,
      from_type: fromType,
      to_type: toType,
      is_active: isActive,
      properties: updatedProperties,
      object_mapper: objectMapper, // НОВЕ поле
      google_drive_files: isGoogleDriveInformationPackageCollectionRoute
        ? selectedGoogleDriveFiles.map((googleDriveFile) => ({
            id: googleDriveFile.id,
            name: googleDriveFile.name,
            mime_type: googleDriveFile.mimeType,
            size: googleDriveFile.size,
            modified_at: googleDriveFile.modifiedTime,
            view_link: googleDriveFile.viewLink,
          }))
        : undefined,
      priority_index: priorityIndex,
    };

    setLoading(true);

    try {
      if (isNew) {
        await createRecordRoute(recordRoute);
        showAlert('success', 'Record route created successfully');
      } else {
        await updateRecordRouteById(recordRouteId, recordRoute);
        showAlert('success', 'Record route updated successfully');
      }
      navigate(`/organizations/${organizationId}/record-route`);
    } catch (error) {
      console.error('Error saving Record Route:', error);
      showAlert('fail', 'Failed to save record route');
    } finally {
      setLoading(false);
    }
  };

  const onTriggerEvent = async () => {
    setLoading(true);

    try {
      if (!isNew) {
        await triggerEventById(recordRouteId);
        showAlert('success', 'Record route triggered successfully');
      }
    } catch (error) {
      console.error('Error trigger Record Route:', error);
      showAlert('fail', 'Failed to trigger record route');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/record-route`);
  };

  const changeFromKey = (key) => {
    if (key === fromKey) return;
    setFromKey(key);
    setFromConnectionObjectMapping({});
  };

  const changeToKey = (key) => {
    if (key === toKey) return;
    setToKey(key);
    setToConnectionObjectMapping({});
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">
            {fromType} {'->'} {toType}
          </h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Route event type</div>
                  <div className="small-font">Select route event type</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomSelect
                    id="route-event-type"
                    label="Route Event Type"
                    options={[
                      { value: 'manual', label: 'Manual' },
                      { value: 'channel_thread', label: 'Channel Thread' },
                      { value: 'channel_thread_message', label: 'Channel Thread Message' },
                      {
                        value: 'channel_thread_message_attachment',
                        label: 'Channel Thread Message Attachment',
                      },
                    ]}
                    value={routeEventType}
                    onChange={(value) => {
                      setRouteEventType(value);
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">From</div>
                  <div className="small-font">Select from type and key</div>
                </div>
                <div className="align-wrapper d-flex align-center gap">
                  <CustomSelect
                    id="from-type"
                    label="Type"
                    options={[...types, { value: 'intercom', label: 'Intercom' }]}
                    value={fromType}
                    onChange={(value) => {
                      if (value === fromType) return;
                      setFromType(value);
                      changeFromKey(undefined);
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                  {fromType === 'connection_object' ? (
                    <ConnectionObjectSelect
                      id="from-key-select"
                      value={fromKey}
                      onChange={changeFromKey}
                      disabled={isDisabled || isTypeDisabled(fromType)}
                      selectedName={selectedFromConnectionObject?.name}
                      selectedLoading={isFromConnectionObjectLoading}
                    />
                  ) : (
                    fromType && (
                      <ListSelect
                        key={fromType}
                        id="from-key-select"
                        label="Name"
                        options={
                          fromType === 'assistant'
                            ? (assistants || []).map((a) => ({ value: a.key, label: a.name }))
                            : fromType === 'channel'
                              ? (channels || []).map((c) => ({ value: c.id, label: c.name }))
                              : fromType === 'document'
                                ? (documents || []).map((d) => ({
                                    value: d.id,
                                    label: d.original_name,
                                  }))
                                : fromType == 'lead'
                                  ? (creatioConnections || []).map((a) => ({
                                      value: a.id,
                                      label: a.name,
                                    }))
                                  : fromType === 'article'
                                    ? (zendeskConnections || []).map((a) => ({
                                        value: a.id,
                                        label: a.name,
                                      }))
                                    : fromType === 'notion'
                                      ? (notionConnections || []).map((a) => ({
                                          value: a.id,
                                          label: a.name,
                                        }))
                                      : fromType === 'google_drive'
                                        ? (googleConnections || []).map((a) => ({
                                            value: a.id,
                                            label: a.name,
                                          }))
                                        : fromType === 'intercom'
                                          ? (intercomConnections || []).map((a) => ({
                                              value: a.id,
                                              label: a.name,
                                            }))
                                          : fromType === 'package_collection'
                                            ? (informationPackageCollections || []).map((dc) => ({
                                                value: dc.id,
                                                label: dc.name,
                                              }))
                                            : fromType === 'document_collection'
                                              ? (documentCollections || []).map((dc) => ({
                                                  value: dc.id,
                                                  label: dc.name,
                                                }))
                                              : []
                        }
                        value={fromKey}
                        onChange={changeFromKey}
                        disabled={isDisabled || isTypeDisabled(fromType)}
                        searchable
                        panelWidth={380}
                      />
                    )
                  )}
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">To</div>
                  <div className="small-font">Select to type and key</div>
                </div>
                <div className="align-wrapper d-flex align-center gap">
                  <CustomSelect
                    id="to-type"
                    label="Type"
                    options={types}
                    value={toType}
                    onChange={(value) => {
                      if (value === toType) return;
                      setToType(value);
                      changeToKey(undefined);
                    }}
                    multiSelect={false}
                    disabled={isDisabled}
                  />
                  {toType === 'connection_object' ? (
                    <ConnectionObjectSelect
                      id="to-key-select"
                      value={toKey}
                      onChange={changeToKey}
                      disabled={isDisabled || isTypeDisabled(toType)}
                      selectedName={selectedToConnectionObject?.name}
                      selectedLoading={isToConnectionObjectLoading}
                    />
                  ) : (
                    toType && (
                      <ListSelect
                        key={toType}
                        id="to-key-select"
                        label="Name"
                        options={
                          toType === 'assistant'
                            ? (assistants || []).map((a) => ({ value: a.key, label: a.name }))
                            : toType === 'channel'
                              ? (channels || []).map((c) => ({ value: c.id, label: c.name }))
                              : toType === 'document'
                                ? (documents || []).map((d) => ({
                                    value: d.id,
                                    label: d.original_name,
                                  }))
                                : toType === 'lead'
                                  ? (creatioConnections || []).map((a) => ({
                                      value: a.id,
                                      label: a.name,
                                    }))
                                  : toType === 'article'
                                    ? (zendeskConnections || []).map((a) => ({
                                        value: a.id,
                                        label: a.name,
                                      }))
                                    : toType === 'notion'
                                      ? (notionConnections || []).map((a) => ({
                                          value: a.id,
                                          label: a.name,
                                        }))
                                      : toType === 'google_drive'
                                        ? (googleConnections || []).map((a) => ({
                                            value: a.id,
                                            label: a.name,
                                          }))
                                        : toType === 'package_collection'
                                          ? (informationPackageCollections || []).map((dc) => ({
                                              value: dc.id,
                                              label: dc.name,
                                            }))
                                          : toType === 'document_collection'
                                            ? (documentCollections || []).map((dc) => ({
                                                value: dc.id,
                                                label: dc.name,
                                              }))
                                            : []
                        }
                        value={toKey}
                        onChange={changeToKey}
                        disabled={isDisabled || isTypeDisabled(toType)}
                        searchable
                        panelWidth={380}
                      />
                    )
                  )}
                </div>
              </div>

              {fromType === 'google_drive' && fromKey && toType === 'package_collection' && (
                <div className="input-row input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Google Drive files</div>
                    <div className="small-font">Select one or more supported files</div>
                    {selectedGoogleDriveFiles.length > 0 && (
                      <div className="small-font">Selected: {selectedGoogleDriveFiles.length}</div>
                    )}
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <Button
                      variant="primary"
                      size="medium"
                      disabled={isDisabled}
                      onClick={() => setIsGoogleDriveFilesModalOpen(true)}
                    >
                      Choose files
                    </Button>
                  </div>
                </div>
              )}

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Priority index</div>
                  <div className="small-font">
                    Lower values are executed first. Use 0 for the highest priority.
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomNumberInput
                    id="priority-index"
                    label=""
                    value={priorityIndex}
                    onChange={setPriorityIndex}
                    min={0}
                    max={100}
                    step={1}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate record route</div>
                  <div className="small-font">
                    Set record route to be active or inactive to enable or disable it
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={false}
                  />
                </div>
              </div>

              {routeEventType === 'manual' && !isNew && (
                <div className="input-row input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Manual Trigger event</div>
                    <div className="small-font">Press button to trigger event</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <Button
                      variant="primary"
                      size="medium"
                      disabled={isNew}
                      onClick={onTriggerEvent}
                    >
                      Trigger
                    </Button>
                  </div>
                </div>
              )}

              {fromType === 'connection_object' && fromKey && selectedFromConnectionObject && (
                <RecordRouteConnectionObjectMapping
                  connectionObject={selectedFromConnectionObject}
                  mapping={fromConnectionObjectMapping}
                  onChange={setFromConnectionObjectMapping}
                  isDisabled={isDisabled}
                />
              )}

              {toType === 'connection_object' && toKey && selectedToConnectionObject && (
                <RecordRouteConnectionObjectMapping
                  connectionObject={selectedToConnectionObject}
                  mapping={toConnectionObjectMapping}
                  onChange={setToConnectionObjectMapping}
                  isDisabled={isDisabled}
                />
              )}
            </div>

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ItemsPickerModal
        isOpen={isGoogleDriveFilesModalOpen}
        onClose={() => setIsGoogleDriveFilesModalOpen(false)}
        title="Google Drive files"
        disabled={isDisabled}
        fetchItems={fetchGoogleDriveFiles}
        columns={googleDriveFileColumns}
        multiSelect={toType === 'package_collection'}
        selectedIds={selectedGoogleDriveFiles.map((googleDriveFile) => googleDriveFile.id)}
        onSaveSelected={handleSaveGoogleDriveFiles}
      />
    </div>
  );
}

function RecordRoute({ canEdit }) {
  return (
    <ConfirmationProvider>
      <RecordRouteApiProvider>
        <RecordRouteDetail canEdit={canEdit} />
      </RecordRouteApiProvider>
    </ConfirmationProvider>
  );
}

export default RecordRoute;
